import React, { forwardRef, useImperativeHandle } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNewThread } from '../../../contexts/NewThreadContext';
import CanvasAssets from './CanvasAssets';
import {
  AIReportingProvider,
  useAIReporting,
  AIReportingTreeNode,
} from './AIReportingWrapper';
import { ThreadLayoutType } from '@dreamai/types';
import './chat-canvas.scss';

// Interface for ChatCanvas ref methods
export interface ChatCanvasRef {
  getTree: () => AIReportingTreeNode[];
}

// Props interface for ChatCanvas
interface ChatCanvasProps {
  layoutType?: ThreadLayoutType;
  discussingObject?: string;
  discussingObjectMatch?: Record<string, any>;
  onHasAssets?: (hasAssets: boolean) => void;
}

// Props interface for CanvasAssetsWithReporting
interface CanvasAssetsWithReportingProps {
  threadId: string;
  layoutType: ThreadLayoutType;
  discussingObject?: string;
  discussingObjectMatch?: Record<string, any>;
  onHasAssets?: (hasAssets: boolean) => void;
}

// CanvasAssets wrapper that has access to AIReporting context
const CanvasAssetsWithReporting = forwardRef<
  { getTree: () => AIReportingTreeNode[] },
  CanvasAssetsWithReportingProps
>(
  (
    {
      threadId,
      layoutType,
      discussingObject,
      discussingObjectMatch,
      onHasAssets,
    },
    ref,
  ) => {
    const aiReporting = useAIReporting();

    // Expose getTree method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        getTree: () => aiReporting.getTree(),
      }),
      [aiReporting],
    );

    return (
      <CanvasAssets
        threadId={threadId}
        layoutType={layoutType}
        discussingObject={discussingObject}
        discussingObjectMatch={discussingObjectMatch}
        onHasAssets={onHasAssets}
      />
    );
  },
);
CanvasAssetsWithReporting.displayName = 'CanvasAssetsWithReporting';

/**
 * ChatCanvas Component
 *
 * Container for the chat canvas that displays workbox items for the current thread
 */
const ChatCanvas = forwardRef<ChatCanvasRef, ChatCanvasProps>(
  (
    {
      layoutType = 'tab',
      discussingObject,
      discussingObjectMatch,
      onHasAssets,
    },
    ref,
  ) => {
    const [searchParams] = useSearchParams();
    const { newThreadId } = useNewThread();
    const threadId = searchParams.get('thread_id') || newThreadId;
    const aiReportingRef = React.useRef<{
      getTree: () => AIReportingTreeNode[];
    }>(null);

    // Expose getTree method to parent components
    useImperativeHandle(
      ref,
      () => ({
        getTree: () => aiReportingRef.current?.getTree() || [],
      }),
      [],
    );

    return (
      <div className='chat-canvas'>
        {threadId && (
          <>
            <div className='canvas-assets-holder'>
              <AIReportingProvider>
                <CanvasAssetsWithReporting
                  threadId={threadId}
                  layoutType={layoutType}
                  discussingObject={discussingObject}
                  discussingObjectMatch={discussingObjectMatch}
                  onHasAssets={onHasAssets}
                  ref={aiReportingRef}
                />
              </AIReportingProvider>
            </div>
          </>
        )}
      </div>
    );
  },
);
ChatCanvas.displayName = 'ChatCanvas';

export default ChatCanvas;
